import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  type DragEndEvent,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Plus, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { ProductoAtributoRow } from '../../types/productoAtributos';
import { LISTADO_ATRIBUTO_PRESETS } from '../../utils/productoAtributos';

const TIPOS: { value: ProductoAtributoRow['tipo']; label: string }[] = [
  { value: 'texto', label: 'Texto' },
  { value: 'numero', label: 'Número' },
];

function newRow(): ProductoAtributoRow {
  const id =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `r-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  return { id, nombre: '', valor: '', tipo: 'texto' };
}

function SortableRow({
  row,
  disabled,
  catalogNames,
  onPatch,
  onRemove,
}: {
  row: ProductoAtributoRow;
  disabled: boolean;
  catalogNames: { nombre: string; tipo: string; unidad: string | null }[];
  onPatch: (id: string, patch: Partial<ProductoAtributoRow>) => void;
  onRemove: (id: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.id,
    disabled,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.85 : 1,
  };

  const [nombreInput, setNombreInput] = useState(row.nombre);
  const [showSuggestions, setShowSuggestions] = useState(false);
  useEffect(() => {
    setNombreInput(row.nombre);
  }, [row.nombre]);

  const suggestions = useMemo(() => {
    const q = nombreInput.trim().toLowerCase();
    if (q.length < 1) {
      return catalogNames.slice(0, 8);
    }
    return catalogNames
      .filter((c) => c.nombre.toLowerCase().includes(q))
      .slice(0, 8);
  }, [catalogNames, nombreInput]);

  const applyCatalogRow = (c: { nombre: string; tipo: string; unidad: string | null }) => {
    const tipo: ProductoAtributoRow['tipo'] = c.tipo === 'numero' ? 'numero' : 'texto';
    onPatch(row.id, {
      nombre: c.nombre,
      tipo,
      unidad: tipo === 'numero' && c.unidad ? String(c.unidad) : undefined,
    });
    setNombreInput(c.nombre);
    setShowSuggestions(false);
  };

  const effectiveTipo: ProductoAtributoRow['tipo'] = row.tipo === 'numero' ? 'numero' : 'texto';

  return (
    <div ref={setNodeRef} style={style} className="flex flex-wrap items-start gap-2 border border-gray-100 rounded-lg p-2 bg-gray-50/80">
      <button
        type="button"
        className="mt-2 p-1 text-gray-400 hover:text-gray-700 cursor-grab active:cursor-grabbing touch-none shrink-0"
        disabled={disabled}
        {...attributes}
        {...listeners}
        aria-label="Reordenar"
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <div className="flex-1 min-w-[140px] relative">
        <label className="sr-only">Nombre de la característica</label>
        <input
          type="text"
          className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm"
          placeholder="Nombre (ej. Color)"
          disabled={disabled}
          value={nombreInput}
          onChange={(e) => {
            const v = e.target.value;
            setNombreInput(v);
            onPatch(row.id, { nombre: v });
            setShowSuggestions(true);
          }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => {
            window.setTimeout(() => setShowSuggestions(false), 180);
          }}
        />
        {showSuggestions && suggestions.length > 0 && (
          <ul className="absolute z-20 mt-0.5 w-full max-h-40 overflow-y-auto rounded-md border border-gray-200 bg-white shadow text-sm">
            {suggestions.map((c) => (
              <li key={c.nombre}>
                <button
                  type="button"
                  className="w-full text-left px-2 py-1.5 hover:bg-blue-50"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    applyCatalogRow(c);
                  }}
                >
                  {c.nombre}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="w-[7.5rem] shrink-0">
        <label className="sr-only">Tipo</label>
        <select
          className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm"
          disabled={disabled}
          value={effectiveTipo}
          onChange={(e) => {
            const tipo = e.target.value as ProductoAtributoRow['tipo'];
            onPatch(row.id, {
              tipo,
              unidad: tipo === 'numero' ? row.unidad : undefined,
            });
          }}
        >
          {TIPOS.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      </div>
      <div className="flex-1 min-w-[120px]">
        {effectiveTipo === 'texto' && (
          <input
            type="text"
            className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm"
            placeholder="Valor"
            disabled={disabled}
            value={row.valor}
            onChange={(e) => onPatch(row.id, { valor: e.target.value })}
          />
        )}
        {effectiveTipo === 'numero' && (
          <div className="flex gap-1">
            <input
              type="text"
              inputMode="decimal"
              className="w-full min-w-0 px-2 py-1.5 border border-gray-200 rounded text-sm"
              placeholder="Valor"
              disabled={disabled}
              value={row.valor}
              onChange={(e) => onPatch(row.id, { valor: e.target.value })}
            />
            <input
              type="text"
              className="w-24 shrink-0 px-2 py-1.5 border border-gray-200 rounded text-sm"
              placeholder="Unidad"
              disabled={disabled}
              value={row.unidad || ''}
              onChange={(e) => onPatch(row.id, { unidad: e.target.value })}
            />
          </div>
        )}
      </div>
      <button
        type="button"
        className="mt-1 p-1 text-gray-400 hover:text-red-600 shrink-0"
        disabled={disabled}
        onClick={() => onRemove(row.id)}
        aria-label="Quitar característica"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

interface AtributosEditorProps {
  empresaId: string | undefined;
  value: ProductoAtributoRow[];
  onChange: (rows: ProductoAtributoRow[]) => void;
  disabled?: boolean;
  /** Botones rápidos solo para listados (presets → filas) */
  listadoPresets?: boolean;
}

export const AtributosEditor: React.FC<AtributosEditorProps> = ({
  empresaId,
  value,
  onChange,
  disabled = false,
  listadoPresets = false,
}) => {
  const [catalog, setCatalog] = useState<
    { nombre: string; tipo: string; unidad: string | null; uso_count: number }[]
  >([]);

  const loadCatalog = useCallback(async () => {
    if (!empresaId) {
      setCatalog([]);
      return;
    }
    const { data, error } = await supabase
      .from('empresa_atributos_catalogo')
      .select('nombre, tipo, unidad, uso_count')
      .eq('empresa_id', empresaId)
      .order('uso_count', { ascending: false })
      .limit(200);
    if (error) {
      console.warn('empresa_atributos_catalogo', error.message);
      setCatalog([]);
      return;
    }
    setCatalog(data || []);
  }, [empresaId]);

  useEffect(() => {
    void loadCatalog();
  }, [loadCatalog]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = value.findIndex((r) => r.id === String(active.id));
    const newIndex = value.findIndex((r) => r.id === String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    onChange(arrayMove(value, oldIndex, newIndex));
  };

  const patchRow = (id: string, patch: Partial<ProductoAtributoRow>) => {
    onChange(value.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const removeRow = (id: string) => {
    onChange(value.filter((r) => r.id !== id));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <label className="block text-sm font-medium text-gray-700">Características</label>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange([...value, newRow()])}
          className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50"
        >
          <Plus className="w-4 h-4" />
          Agregar característica
        </button>
      </div>
      {listadoPresets && !disabled && (
        <div className="flex flex-wrap gap-2">
          {(['Inmueble', 'Vehículo', 'Empleo'] as const).map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => {
                const defs = LISTADO_ATRIBUTO_PRESETS[preset] || [];
                onChange(
                  defs.map((d) => ({
                    id:
                      typeof crypto !== 'undefined' && crypto.randomUUID
                        ? crypto.randomUUID()
                        : `p-${d.nombre}-${Math.random()}`,
                    nombre: d.nombre,
                    valor: d.valor,
                    tipo: d.tipo,
                  }))
                );
              }}
              className="px-3 py-1 text-xs border border-gray-200 rounded-full hover:bg-gray-50"
            >
              Plantilla {preset}
            </button>
          ))}
        </div>
      )}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={value.map((r) => r.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {value.map((row) => (
              <SortableRow
                key={row.id}
                row={row}
                disabled={!!disabled}
                catalogNames={catalog}
                onPatch={patchRow}
                onRemove={removeRow}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      {value.length === 0 && (
        <p className="text-xs text-gray-500">Sin características. Usa «Agregar característica» o una plantilla de listado.</p>
      )}
    </div>
  );
};
